import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './icons';

export type MultiComboboxProps<T> = {
  label: string;
  /** All entries; filtering happens here (client side), so the list opens immediately with everything. */
  items: readonly T[];
  getId: (item: T) => string;
  /** Plain text of an entry: chip text and screen reader name. */
  getLabel: (item: T) => string;
  selected: readonly string[];
  onChange: (ids: string[]) => void;
  matches: (item: T, query: string) => boolean;
  /** Rich content of one row (name plus the details that tell entries apart). */
  renderItem: (item: T, state: { selected: boolean; active: boolean }) => ReactNode;
  /** Chip text for selected ids that are not in `items` (yet), e.g. just created. */
  fallbackLabel?: (id: string) => string;
  placeholder?: string;
  hint?: ReactNode;
  error?: string;
  loading?: boolean;
  /** Row at the end of the list ("Neue Person anlegen"), gets the typed text. */
  create?: { label: (query: string) => string; onCreate: (query: string) => void };
  disabled?: boolean;
  /** Rows rendered at once; more follow while scrolling or moving with the keyboard (hundreds of entries). */
  pageSize?: number;
  autoFocus?: boolean;
};

const CREATE = '__create__';

/**
 * Multi-select combobox (WAI-ARIA 1.2 combobox + multi-select listbox): the input filters, the list opens on focus
 * with all entries and checkboxes, chips above show the selection and remove it. Keyboard: arrows/PageUp/PageDown
 * move, Enter toggles, Escape closes, Backspace in the empty input removes the last chip. The list is part of the
 * flow (no overlay), so it is never clipped inside dialogs.
 */
export function MultiCombobox<T>({
  label,
  items,
  getId,
  getLabel,
  selected,
  onChange,
  matches,
  renderItem,
  fallbackLabel,
  placeholder,
  hint,
  error,
  loading,
  create,
  disabled,
  pageSize = 60,
  autoFocus,
}: MultiComboboxProps<T>) {
  const { t } = useTranslation('common');
  const id = useId();
  const inputId = `${id}-input`;
  const listId = `${id}-list`;
  const msgId = `${id}-msg`;
  const wrap = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [limit, setLimit] = useState(pageSize);

  const filtered = useMemo(
    () => (query.trim() ? items.filter((i) => matches(i, query)) : [...items]),
    [items, matches, query],
  );
  const shown = filtered.slice(0, limit);
  // index space: 0..filtered.length-1 = entries, filtered.length = the create row
  const keyAt = (i: number) => (i < filtered.length ? getId(filtered[i]!) : create ? CREATE : undefined);
  const byId = useMemo(() => new Map(items.map((i) => [getId(i), i])), [items, getId]);
  const chipText = (sid: string) => {
    const it = byId.get(sid);
    return it ? getLabel(it) : (fallbackLabel?.(sid) ?? sid);
  };
  const optId = (k: string) => `${id}-opt-${k}`;

  useEffect(() => {
    setActive(0);
    setLimit(pageSize);
  }, [query, pageSize]);
  // an open list closes on a click / tap or focus anywhere outside and on Escape wherever the focus is
  // (captured first, so a surrounding dialog stays open)
  useEffect(() => {
    if (!open) return;
    const outside = (e: Event) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('touchstart', outside);
    document.addEventListener('focusin', outside);
    window.addEventListener('keydown', onEsc, true);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('touchstart', outside);
      document.removeEventListener('focusin', outside);
      window.removeEventListener('keydown', onEsc, true);
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const k = keyAt(active);
    if (k) document.getElementById(optId(k))?.scrollIntoView?.({ block: 'nearest' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, open]);

  const toggle = (sid: string) =>
    onChange(selected.includes(sid) ? selected.filter((x) => x !== sid) : [...selected, sid]);
  const choose = (k: string | undefined) => {
    if (!k) return;
    if (k === CREATE) {
      setOpen(false);
      create?.onCreate(query.trim());
      return;
    }
    toggle(k);
  };
  const move = (to: number) => {
    const last = filtered.length + (create ? 1 : 0) - 1;
    if (last < 0) return;
    const next = Math.max(0, Math.min(last, to));
    // moving towards the end of the rendered rows renders more first, so the active row always exists
    if (next >= limit - 1 && limit < filtered.length)
      setLimit(Math.min(filtered.length, Math.max(limit + pageSize, next + pageSize)));
    setActive(next);
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      else move(active + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (open) move(active - 1);
    } else if (e.key === 'PageDown' && open) {
      e.preventDefault();
      move(active + 10);
    } else if (e.key === 'PageUp' && open) {
      e.preventDefault();
      move(active - 10);
    } else if (e.key === 'Enter') {
      if (!open) return;
      e.preventDefault();
      choose(keyAt(active));
    } else if (e.key === 'Escape' && open) {
      // close only the list, not the surrounding dialog
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    } else if (e.key === 'Backspace' && !query && selected.length) {
      onChange(selected.slice(0, -1));
    } else if (e.key === 'Tab') setOpen(false);
  };
  const onScroll = () => {
    const el = list.current;
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 80 && limit < filtered.length)
      setLimit((l) => l + pageSize);
  };
  const activeKey = open ? keyAt(active) : undefined;

  return (
    <div className={`field combo ${open ? 'open' : ''}`} ref={wrap}>
      <label htmlFor={inputId}>{label}</label>
      {selected.length > 0 && (
        <ul className="combo-chips" aria-label={t('ui.combo.selected', { label })}>
          {selected.map((sid) => (
            <li key={sid} className="combo-chip">
              <span>{chipText(sid)}</span>
              <button
                type="button"
                aria-label={t('ui.combo.remove', { name: chipText(sid) })}
                disabled={disabled}
                onClick={() => {
                  onChange(selected.filter((x) => x !== sid));
                  input.current?.focus();
                }}
              >
                <Icon name="close" size={14} strokeWidth={2} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="combo-box">
        <Icon name="search" size={18} strokeWidth={1.8} />
        <input
          ref={input}
          id={inputId}
          className={`input ${error ? 'invalid' : ''}`}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeKey ? optId(activeKey) : undefined}
          aria-invalid={!!error || undefined}
          aria-describedby={error || hint ? msgId : undefined}
          // a search field, not a login: keep password managers (Bitwarden, 1Password, LastPass) out of it
          type="text"
          name={`${id}-filter`}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          data-bwignore="true"
          data-1p-ignore="true"
          data-lpignore="true"
          data-form-type="other"
          autoFocus={autoFocus}
          placeholder={placeholder}
          disabled={disabled}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onKeyDown={onKey}
        />
        <button
          type="button"
          className="combo-toggle"
          tabIndex={-1}
          aria-label={t('ui.combo.toggle', { label })}
          aria-expanded={open}
          aria-controls={listId}
          disabled={disabled}
          onClick={() => {
            setOpen((o) => !o);
            input.current?.focus();
          }}
        >
          <Icon
            name="chevronDown"
            size={18}
            strokeWidth={1.8}
            className={`menu-caret ${open ? 'open' : ''}`}
          />
        </button>
      </div>
      <div className="combo-pop" hidden={!open}>
        <ul
          ref={list}
          id={listId}
          role="listbox"
          aria-multiselectable="true"
          aria-label={label}
          className="combo-list"
          onScroll={onScroll}
        >
          {shown.map((it, i) => {
            const k = getId(it);
            const sel = selected.includes(k);
            return (
              <li
                key={k}
                id={optId(k)}
                role="option"
                aria-selected={sel}
                className={`combo-opt ${i === active ? 'active' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onMouseMove={() => i !== active && setActive(i)}
                onClick={() => toggle(k)}
              >
                <span className="combo-check" aria-hidden>
                  {sel && <Icon name="check" size={14} strokeWidth={2.4} />}
                </span>
                <span className="combo-text">{renderItem(it, { selected: sel, active: i === active })}</span>
              </li>
            );
          })}
          {create && (
            <li
              id={optId(CREATE)}
              role="option"
              aria-selected={false}
              className={`combo-opt combo-create ${activeKey === CREATE ? 'active' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onMouseMove={() => setActive(filtered.length)}
              onClick={() => choose(CREATE)}
            >
              <span className="combo-check plus" aria-hidden>
                <Icon name="plus" size={14} strokeWidth={2.2} />
              </span>
              <span className="combo-text">{create.label(query.trim())}</span>
            </li>
          )}
        </ul>
        {!loading && !filtered.length && <p className="combo-empty">{t('ui.combo.noResults')}</p>}
        {loading && <p className="combo-empty">{t('ui.loadingLabel')}</p>}
      </div>
      <span className="sr-only" aria-live="polite">
        {open && !loading ? t('ui.combo.results', { count: filtered.length }) : ''}
      </span>
      {error ? (
        <span className="err" id={msgId} role="alert">
          {error}
        </span>
      ) : hint ? (
        <span className="hint" id={msgId}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}
